import { Typography } from "@mui/material";
import { StateFieldsForm, StateFieldsFormProps } from "components/Forms/StateFieldsForm";
import { SubSection } from "components/SubSection";
import { ProgramArea } from "generated-graphql/graphql";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";

import { FacilityDetailsInput } from "./FacilityProfile";
import { humanizeStateFieldLabel } from "./humanizeStateFieldLabel";
import { useFacilityStateFields } from "./useFacility";

interface FacilityStateFieldsFormProps extends StateFieldsFormProps {
  jurisdictions?: string[];
  programArea?: ProgramArea;
  /**
   * State-field keys to keep regardless of the `programArea` filter. Used by the
   * waste submission stepper to surface a small set of EPCRA fields (e.g. WA's
   * UBI) alongside the RCRA fields. Only applies when `programArea` is RCRA.
   */
  additionalKeys?: string[];
  /**
   * Remove `SubSection`'s default horizontal inset so the fields sit flush-left
   * with the surrounding heading. Used by the waste submission stepper, which
   * renders this form inline under a step header rather than inside a dialog.
   * @default false
   */
  flush?: boolean;
}

export function FacilityStateFieldsForm(props: FacilityStateFieldsFormProps) {
  const form = useFormContext<FacilityDetailsInput>();
  const { facilityId } = useParams<{ facilityId: string }>();
  const { jurisdictions, programArea, additionalKeys, flush, ...rest } = props;
  const subSectionStyle = flush ? { marginLeft: 0, marginRight: 0 } : undefined;

  const { error } = useFacilityStateFields(facilityId, jurisdictions, {
    onCompleted: (data) => {
      if (
        // if we get back an array, empty or not, set it
        data?.facilityStateFields !== undefined &&
        data?.facilityStateFields !== null
      ) {
        let fields = data.facilityStateFields;
        if (programArea === ProgramArea.Rcra) {
          fields = fields.filter(
            (f) => f.type === ProgramArea.Rcra || !!additionalKeys?.includes(f.key)
          );
        } else if (programArea === ProgramArea.Epcra) {
          fields = fields.filter((f) => f.type !== ProgramArea.Rcra);
        }
        const mappedFields = fields.map((field) => ({
          ...field,
          label: humanizeStateFieldLabel(field.label, field.key),
          value: field.value ?? null,
        }));

        form.setValue("stateFields", mappedFields);
      }
    },
  });

  if (error) return <div>Error loading state fields</div>;

  if (jurisdictions?.includes("CA")) {
    return (
      <SubSection style={subSectionStyle}>
        <Typography>
          Because this facility is in California, the California HMBP questions will appear in the
          facility's Tier II report in Chemicals.
        </Typography>
      </SubSection>
    );
  }

  if (props.fields?.length === 0) {
    return (
      <SubSection style={subSectionStyle}>
        <Typography>No additional information needed.</Typography>
      </SubSection>
    );
  } else {
    return (
      <SubSection style={subSectionStyle}>
        <StateFieldsForm {...rest} />
      </SubSection>
    );
  }
}
